import Constants from 'expo-constants';

import type { ApplePayContext, PipelineStepContext, PipelineStepResult } from '..';
import { PaymentProcessor, PipelineBaseStep } from '..';

declare global {
  interface Window {
    eProtect: any;
  }
}

interface IWorldpayConfig {
  eProtectUrl: string;
  paypageId: string;
}

interface IEProtectPayPageResponse {
  paypageRegistrationId: string;
  type: string;
  bin: string;
  lastFour: string;
  expMonth?: string;
  expYear?: string;
  message?: string;
  response?: string;
}

/**
 * Converts Apple Pay token for Worldpay
 *
 * For Worldpay, Apple Pay returns encrypted payment data that needs to be
 * exchanged for a low value token via the eProtect API.
 *
 * This step:
 * 1. Extracts Apple Pay payment details
 * 2. Calls Worldpay eProtect API to exchange for low value token
 * 3. Updates the context with the low value token
 *
 * Worldpay expects:
 * - applePayDetails.paymentData = Worldpay low value token
 * - Other fields remain for backend processing
 */
export class WorldpayApplePayConversionStep extends PipelineBaseStep {
  readonly name = 'WorldpayApplePayConversionStep';

  async execute(context: PipelineStepContext): Promise<PipelineStepResult> {
    // Only run for Apple Pay with Worldpay
    if (context.paymentMethod !== 'applePay' || context.processor !== PaymentProcessor.WORLDPAY) {
      return this.success();
    }

    const applePayContext = context as ApplePayContext;
    const { applePayDetails } = applePayContext;

    this.logger.log('[ApplePay] Converting token for Worldpay', {
      processor: context.processor,
      hasPaymentData: !!applePayDetails.paymentData,
    });

    try {
      const worldpayConfig = this.getWorldpayConfig();

      const lowValueToken = await this.requestWorldpayLowValueToken(
        applePayDetails,
        worldpayConfig,
        context.rbiOrderId,
        context.userId
      );

      // Update the payment data with the low value token
      const convertedDetails = {
        ...applePayDetails,
        paymentData: lowValueToken,
      };

      this.logger.log('[ApplePay] Worldpay token conversion complete', {
        tokenLength: lowValueToken.length,
        paymentType: convertedDetails.PaymentMethodData.paymentType,
      });

      // Return updated context with converted details (match legacy: no bin/last4 in commit)
      return this.success({
        applePayDetails: convertedDetails,
      } as Partial<ApplePayContext>);
    } catch (error) {
      this.logger.logError('[ApplePay] Worldpay token conversion failed', error);

      return this.error(
        'WORLDPAY_APPLE_PAY_CONVERSION_FAILED',
        error instanceof Error ? error.message : 'Unknown error',
        'Failed to process Apple Pay payment. Please try again. Error Code: (Worldpay Conversion Failed)',
        'WorldpayApplePayConversionStep'
      );
    }
  }

  private getWorldpayConfig(): IWorldpayConfig {
    const eas = Constants?.expoConfig?.extra?.eas;
    const worldpayConfig = eas?.US?.worldpay || eas?.CA?.worldpay;

    if (!worldpayConfig?.eProtectUrl || !worldpayConfig?.paypageId) {
      throw new Error(
        'Worldpay configuration not found or incomplete. Ensure expoConfig.extra.eas.US.worldpay or .CA.worldpay has eProtectUrl and paypageId.'
      );
    }

    return {
      eProtectUrl: worldpayConfig.eProtectUrl,
      paypageId: worldpayConfig.paypageId,
    };
  }

  private async requestWorldpayLowValueToken(
    applePayDetails: any,
    worldpayConfig: IWorldpayConfig,
    rbiOrderId: string,
    userId?: string
  ): Promise<string> {
    const merchantOrderId = rbiOrderId.slice(0, 20);
    // Use extra.eas.env and buildRegion; never send literal "undefined" in reportGroup (Worldpay rejects it).
    const env = (Constants?.expoConfig?.extra?.eas?.env ?? '') as string;
    const buildRegion = (Constants?.expoConfig?.extra?.eas?.buildRegion ?? 'US') as string;
    const merchantReportGroup = `${env}-FHS-${buildRegion}`.toUpperCase();

    const accountNumber = userId ?? '';

    this.logger.log('[ApplePay] Requesting Worldpay low value token', {
      merchantOrderId,
      merchantReportGroup,
      eProtectUrl: worldpayConfig.eProtectUrl,
    });

    // Use eProtect SDK for web (CORS), direct fetch for native (no CORS in RN)
    if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
      // WEB: Use eProtect SDK
      return this.requestViaEProtectSDK(
        applePayDetails,
        worldpayConfig,
        merchantOrderId,
        merchantReportGroup,
        accountNumber
      );
    } else {
      // NATIVE: Use direct fetch (React Native fetch, no browser CORS)
      return this.requestViaDirectFetch(
        applePayDetails,
        worldpayConfig,
        merchantOrderId,
        merchantReportGroup,
        accountNumber
      );
    }
  }

  private async requestViaEProtectSDK(
    applePayDetails: any,
    worldpayConfig: IWorldpayConfig,
    merchantOrderId: string,
    merchantReportGroup: string,
    accountNumber: string
  ): Promise<string> {
    // Load eProtect SDK script
    const scriptUrl = `${worldpayConfig.eProtectUrl}/eProtect/eProtect-api3.js`;
    await this.loadScript(scriptUrl);

    const preRequest = {
      paypageId: worldpayConfig.paypageId,
      reportGroup: merchantReportGroup,
      orderId: merchantOrderId,
      id: merchantOrderId,
      accountNumber,
      url: worldpayConfig.eProtectUrl,
      applepay: {
        data: applePayDetails?.data ?? '',
        signature: applePayDetails?.signature ?? '',
        version: applePayDetails?.version ?? '',
        header: {
          applicationData: '',
          ephemeralPublicKey: applePayDetails?.ephemeralPublicKey ?? '',
          publicKeyHash: applePayDetails?.publicKeyHash ?? '',
          transactionId: applePayDetails?.transactionId ?? '',
        },
      },
    };

    const request = {};

    this.logger.log('[ApplePay] Calling eProtect SDK (Web)');

    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.eProtect) {
        reject(new Error('eProtect SDK not loaded'));
        return;
      }

      window.eProtect().sendToEprotect(
        preRequest,
        request,
        (data: IEProtectPayPageResponse) => {
          this.logger.log('[ApplePay] Successfully received Worldpay low value token', {
            tokenLength: data.paypageRegistrationId?.length || 0,
          });
          resolve(data.paypageRegistrationId);
        },
        (error: any) => {
          this.logger.logError('[ApplePay] eProtect request failed', error);
          reject(new Error(`eProtect request failed: ${error}`));
        },
        () => {
          this.logger.logError('[ApplePay] eProtect request timeout');
          reject(new Error('eProtect request timeout'));
        },
        5000
      );
    });
  }

  private async requestViaDirectFetch(
    applePayDetails: any,
    worldpayConfig: IWorldpayConfig,
    merchantOrderId: string,
    merchantReportGroup: string,
    accountNumber: string
  ): Promise<string> {
    const data: Record<string, string> = {
      paypageId: worldpayConfig.paypageId,
      reportGroup: merchantReportGroup,
      orderId: merchantOrderId,
      id: merchantOrderId,
      accountNumber,
      'applepay.data': applePayDetails?.data ?? '',
      'applepay.signature': applePayDetails?.signature ?? '',
      'applepay.version': applePayDetails?.version ?? '',
      'applepay.header.applicationData': '',
      'applepay.header.ephemeralPublicKey': applePayDetails?.ephemeralPublicKey ?? '',
      'applepay.header.publicKeyHash': applePayDetails?.publicKeyHash ?? '',
      'applepay.header.transactionId': applePayDetails?.transactionId ?? '',
    };

    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    };

    const paypageUrl = `${worldpayConfig.eProtectUrl}/eProtect/paypage`;

    this.logger.log('[ApplePay] Calling Worldpay eProtect API (Native fetch)', {
      paypageUrl,
      dataKeys: Object.keys(data),
    });

    // Use encodeFormData (string) instead of URLSearchParams object.
    // React Native's fetch (XMLHttpRequest) may not serialize URLSearchParams correctly,
    // sending "[object URLSearchParams]" as the literal body instead of encoded form data.
    const body = encodeFormData(data);
    const response = await fetch(paypageUrl, {
      method: 'POST',
      headers,
      body,
    });

    const text = await response.text();

    if (!response.ok) {
      this.logger.logError('[ApplePay] Worldpay eProtect API error response', {
        status: response.status,
        statusText: response.statusText,
        body: text,
        reportGroup: merchantReportGroup,
      });
      throw new Error(
        `Worldpay eProtect API returned ${response.status}: ${response.statusText}. Body: ${text.slice(0, 500)}`
      );
    }

    let resData: IEProtectPayPageResponse;

    try {
      resData = JSON.parse(text);
    } catch (error) {
      this.logger.logError('[ApplePay] Worldpay returned invalid JSON response', {
        status: response.status,
        text,
        error,
      });
      throw new Error('Invalid JSON response from Worldpay eProtect');
    }

    if (!resData.paypageRegistrationId) {
      const errorMessage = resData.message || resData.response || 'Unknown error from Worldpay';
      this.logger.logError('[ApplePay] Worldpay eProtect did not return registration ID', {
        status: response.status,
        response: resData,
        rawBody: text,
        reportGroup: merchantReportGroup,
      });
      throw new Error(`Worldpay eProtect error: ${errorMessage}`);
    }

    this.logger.log('[ApplePay] Successfully received Worldpay low value token', {
      tokenLength: resData.paypageRegistrationId.length,
    });

    return resData.paypageRegistrationId;
  }

  private async loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window not available - cannot load script'));
        return;
      }

      // Check if already loaded
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }
}

/**
 * Encodes a key-value object into a URL-encoded form data string.
 * Matches workspace/frontend encodeFormData implementation.
 * Used instead of URLSearchParams because React Native's fetch (XMLHttpRequest)
 * may not properly serialize URLSearchParams objects as the request body.
 */
function encodeFormData(data: Record<string, string>): string {
  const formBody: string[] = [];
  for (const property in data) {
    const encodedKey = encodeURIComponent(property);
    const encodedValue = encodeURIComponent(data[property]);
    formBody.push(encodedKey + '=' + encodedValue);
  }
  return formBody.join('&');
}
